import {
  Check,
  Copy,
  EllipsisVertical,
  Globe,
  Lock,
  MessageSquareMore,
  PanelRightClose,
  RefreshCw,
  Share2,
  Trash2,
} from 'lucide-react';
import { useCallback, useState } from 'react';

import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/_shared/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/_shared/components/ui/popover';
import { Switch } from '@/_shared/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/_shared/components/ui/tooltip';

import { usePublicSharingPolicy } from '../hooks/usePublicSharingPolicy';
import type { Dashboard } from '../types';
import { PublishConfirmDialog } from './PublishConfirmDialog';

interface Props {
  dashboard: Dashboard;
  isAdmin?: boolean;
  onToggleChat?: () => void;
  chatOpen?: boolean;
  onDelete?: () => void;
  deletePending?: boolean;
  /** Plaintext share token, available only immediately after a mint
   * (publish or regenerate) — the cached dashboard query never carries it. */
  plaintextToken?: string | null;
  onPublish?: () => void;
  onUnpublish?: () => void;
  onRegenerate?: () => void;
  regeneratePending?: boolean;
  /** True while the publish/unpublish mutation is in flight. The Switch is
   * disabled while this is true so a double-click can't kick off two
   * mutations (the second of which would re-open the publish dialog with a
   * stale isPublic). */
  privacyPending?: boolean;
}

function SharePopover({
  dashboard,
  plaintextToken,
  onPublish,
  onUnpublish,
  onRegenerate,
  regeneratePending = false,
  privacyPending = false,
}: {
  dashboard: Dashboard;
  plaintextToken: string | null;
  onPublish: () => void;
  onUnpublish: () => void;
  onRegenerate: () => void;
  regeneratePending?: boolean;
  privacyPending?: boolean;
}): React.ReactElement {
  const { publicSharingEnabled } = usePublicSharingPolicy();
  const [copied, setCopied] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const publicUrl = plaintextToken ? `${window.location.origin}/pub/${plaintextToken}` : '';

  const handleCopy = useCallback((): void => {
    if (!publicUrl) return;
    navigator.clipboard
      .writeText(publicUrl)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        /* clipboard API unavailable */
      });
  }, [publicUrl]);

  const handleToggle = useCallback(
    (next: boolean): void => {
      if (next) {
        // Always confirm before flipping public — the publish dialog is a
        // hard read of "this dashboard is about to leave the workspace".
        setConfirmOpen(true);
      } else {
        onUnpublish();
      }
    },
    [onUnpublish],
  );

  const handleConfirm = useCallback((): void => {
    setConfirmOpen(false);
    onPublish();
  }, [onPublish]);

  // Disable when the user can NEITHER publish NOR unpublish. Going public
  // is gated on the workspace flag; going private is always allowed so an
  // owner can clear an orphan token after an admin disables sharing. Also
  // disable while a mutation is in flight so a double-click can't kick off
  // a second mutation against a stale isPublic.
  const switchDisabled = (!publicSharingEnabled && !dashboard.isPublic) || privacyPending || regeneratePending;
  const switchEl = (
    <Switch
      checked={dashboard.isPublic}
      onCheckedChange={handleToggle}
      disabled={switchDisabled}
      aria-label="Toggle public visibility"
    />
  );

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            <Share2 className="size-3.5" />
            Share
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="end">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-foreground">
                {dashboard.isPublic ? (
                  <Globe className="size-4 text-muted-foreground" />
                ) : (
                  <Lock className="size-4 text-muted-foreground" />
                )}
                <span>{dashboard.isPublic ? 'Public' : 'Private'}</span>
              </div>
              {switchDisabled ? (
                <TooltipProvider>
                  <Tooltip>
                    {/* Wrap in a span so the tooltip trigger still receives
                        hover events even when the underlying Switch is
                        disabled (disabled buttons don't fire pointer events). */}
                    <TooltipTrigger asChild>
                      <span>{switchEl}</span>
                    </TooltipTrigger>
                    <TooltipContent>Public sharing is disabled by your workspace admin</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                switchEl
              )}
            </div>

            {dashboard.isPublic && !publicSharingEnabled && (
              <div className="rounded-md border border-border bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                This link is currently inaccessible. An admin disabled public sharing for this workspace. Unpublish to
                clear the existing token.
              </div>
            )}

            {dashboard.isPublic && publicSharingEnabled && plaintextToken && (
              <div className="space-y-2">
                <label className="text-xs text-muted-foreground">Share link</label>
                <div className="flex items-center gap-2">
                  <input
                    readOnly
                    value={publicUrl}
                    className="flex-1 truncate rounded-md border border-border bg-muted px-3 py-1.5 font-mono text-xs text-foreground"
                    onFocus={(e) => e.target.select()}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="size-8 shrink-0"
                    onClick={handleCopy}
                    aria-label="Copy link"
                  >
                    {copied ? <Check className="size-3.5 text-green-700" /> : <Copy className="size-3.5" />}
                  </Button>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full gap-2 text-xs"
                  onClick={onRegenerate}
                  disabled={regeneratePending}
                >
                  <RefreshCw className="size-3.5" />
                  Regenerate link
                </Button>
              </div>
            )}

            {dashboard.isPublic && publicSharingEnabled && !plaintextToken && (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  The share link is hidden after the popover closes. Regenerate to mint a new one.
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full gap-2 text-xs"
                  onClick={onRegenerate}
                  disabled={regeneratePending}
                >
                  <RefreshCw className="size-3.5" />
                  Regenerate link
                </Button>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      <PublishConfirmDialog open={confirmOpen} onConfirm={handleConfirm} onCancel={() => setConfirmOpen(false)} />
    </>
  );
}

export function DashboardHeader({
  dashboard,
  isAdmin = false,
  onToggleChat,
  chatOpen = false,
  onDelete,
  deletePending,
  plaintextToken = null,
  onPublish,
  onUnpublish,
  onRegenerate,
  regeneratePending,
  privacyPending,
}: Props): React.ReactElement {
  // The share popover is gated on isAdmin. The Dashboard view above us is
  // already passing isAdmin = (workspaceAdmin || dashboard.createdBy === user.id),
  // so creators-without-admin still get the popover from this single flag.
  // Callbacks default to no-ops so tests / read-only renders still mount the
  // popover; the parent wires real mutations on Dashboard.tsx.
  const canShare = isAdmin;
  const noop = (): void => {
    // No-op fallback — the parent owns the real mutation handlers.
  };

  return (
    <div className="flex items-center justify-between border-b border-border/50 px-6 py-4">
      <div className="min-w-0">
        <h1 className="truncate text-lg font-semibold text-foreground">{dashboard.title}</h1>
        {dashboard.description && (
          <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{dashboard.description}</p>
        )}
      </div>

      <div className="ml-4 flex shrink-0 items-center gap-2">
        {canShare && (
          <SharePopover
            dashboard={dashboard}
            plaintextToken={plaintextToken}
            onPublish={onPublish ?? noop}
            onUnpublish={onUnpublish ?? noop}
            onRegenerate={onRegenerate ?? noop}
            regeneratePending={regeneratePending}
            privacyPending={privacyPending}
          />
        )}

        {onToggleChat && (
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={onToggleChat}
            aria-label={chatOpen ? 'Close dashboard assistant' : 'Ask about this dashboard'}
            aria-expanded={chatOpen}
            aria-controls="dashboard-chat-sidebar"
            title="Ask about this dashboard (⌘⇧K)"
          >
            {chatOpen ? <PanelRightClose className="h-3.5 w-3.5" /> : <MessageSquareMore className="h-3.5 w-3.5" />}
            {chatOpen ? 'Close' : 'Ask'}
          </Button>
        )}

        {isAdmin && onDelete && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="px-2">
                <EllipsisVertical className="size-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                disabled={deletePending}
                onClick={() => onDelete()}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 size-3.5" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {!isAdmin && dashboard.isPublic && (
          <Badge variant="secondary" className="text-xs">
            Powered by Rudder AI · Private Beta
          </Badge>
        )}
      </div>
    </div>
  );
}
